import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getAutoCreateOptions, getDefaultDataSources } from './DataConfigDefaultsParamDefs'

import ConfirmDialog from 'components/common/ConfirmDialog'
import DataConfigParameterDisplay from '../DataConfigModal/DataConfigParameterDisplay'
import StatusSnackbar from 'components/common/StatusSnackbar'
import useDataConfig from 'components/common/hooks/useDataConfig'
import useErrorModels from 'components/common/hooks/useErrorModels'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import useUnits from 'components/common/hooks/useUnits'

const DataConfigDefaultsModal = ({ open, onClose }) => {
  const _isMounted = useRef(false)

  const { getWindowBarColor } = useInnovaTheme()
  const { unitList } = useUnits()
  const { databaseOrg, userRole } = useInnovaAuth()

  const [changesMade, setChangesMade] = useState(false)
  const [defaultConfig, setDefaultConfig] = useState(null)
  const [status, setStatus] = useState({ show: false, severity: 'info', message: '' })
  const [confirmHistorical, setConfirmHistorical] = useState({ show: false, title: '' })
  const [confirmSync, setConfirmSync] = useState({ show: false, title: '' })

  const {
    isFetchingDefaults,
    isUpdatingDefaults,
    isSyncingWellsAndReporting,
    isImportingAllHistoricalData,
    fetchDefaultDataSources,
    updateDefaultDataSources,
    syncWellsAndReporting,
    importAllHistoricalEDRData,
  } = useDataConfig()
  const { errorModelHeaders, isLoadingErrorModelHeaders } = useErrorModels()

  const showResult = (res) => {
    setStatus({
      show: true,
      severity: res.success ? 'success' : 'error',
      message: res.message,
    })
  }

  useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onSyncWellsAndReporting = () => {
    setConfirmSync({
      show: true,
      title: 'Sync Wells and Reporting',
      text: `Are you sure you want to sync wells and reporting data?`,
      onConfirm: handleSyncWellsAndReporting,
    })
  }

  const handleSyncWellsAndReporting = async () => {
    if (isSyncingWellsAndReporting) return

    const res = await syncWellsAndReporting(databaseOrg)

    showResult(res)
  }

  const handleFetchDataAcqConfig = async () => {
    if (isFetchingDefaults) return

    const res = await fetchDefaultDataSources(databaseOrg)
    if (!res.success) {
      showResult(res)
    }

    if (res.success) {
      setDefaultConfig(res.data)
    }
  }

  useEffect(() => {
    handleFetchDataAcqConfig()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const dataSourcesDefs = useMemo(
    () => getDefaultDataSources(defaultConfig, unitList, databaseOrg, errorModelHeaders),
    [defaultConfig, databaseOrg, unitList, errorModelHeaders],
  )

  const showImportAllHistoricalEDRData = dataSourcesDefs?.some(
    (def) => def.tag === 'edr' && def.value === 'NOV Welldata',
  )

  const autoCreateDefs = useMemo(
    () => getAutoCreateOptions(defaultConfig, unitList, databaseOrg, errorModelHeaders),
    [defaultConfig, databaseOrg, unitList, errorModelHeaders],
  )

  const handleSave = async () => {
    if (isUpdatingDefaults) return
    const res = await updateDefaultDataSources(defaultConfig, databaseOrg)
    showResult(res)
  }

  const handleUpdate = async (data) => {
    if (data == null || !defaultConfig) return
    if (typeof data !== 'object') return

    if (!data.hasOwnProperty('tag')) return

    setDefaultConfig((prevData) => ({ ...prevData, [data.tag]: data.value }))
    setChangesMade(true)
  }

  const onImportHistorical = () => {
    setConfirmHistorical({
      show: true,
      title: 'Import Historical EDR Data',
      text: `Are you sure you want to import all historical EDR data? This will overwrite any existing data for wells found.`,
      onConfirm: handleImportAllHistoricalEDRData,
    })
  }

  const handleImportAllHistoricalEDRData = async () => {
    if (isImportingAllHistoricalData) return

    const res = await importAllHistoricalEDRData(databaseOrg)

    showResult(res)
  }

  return (
    <>
      {confirmHistorical.show ? (
        <ConfirmDialog
          title={confirmHistorical?.title}
          open={confirmHistorical?.show}
          setOpen={() => setConfirmHistorical({ show: false })}
          onConfirm={confirmHistorical.onConfirm}>
          {confirmHistorical?.text}
        </ConfirmDialog>
      ) : null}
      {confirmSync.show ? (
        <ConfirmDialog
          title={confirmSync?.title}
          open={confirmSync?.show}
          setOpen={() => setConfirmSync({ show: false })}
          onConfirm={confirmSync.onConfirm}>
          {confirmSync?.text}
        </ConfirmDialog>
      ) : null}

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth='xl'
        PaperProps={{
          sx: {
            backgroundColor: getWindowBarColor(),
            display: 'flex',
            flexDirection: 'column',
            height: '90vh',
            width: '85vw',
            overflow: 'hidden',
          },
        }}>
        <DialogTitle>{`Data Config Defaults - ${databaseOrg}`}</DialogTitle>
        <DialogContent dividers sx={{ padding: '0px' }}>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '16px' },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: getWindowBarColor(),
                  boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                  WebkitBoxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#A0A0A0',
                  border: '4px solid ' + getWindowBarColor(),
                  borderRadius: '16px',
                },
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  width: '100%',
                  flexWrap: 'wrap',
                  padding: 2,
                  height: '425px',
                }}>
                <DataConfigParameterDisplay title='Data Sources' defs={dataSourcesDefs} onChange={handleUpdate} />
                <DataConfigParameterDisplay title='Auto Create Options' defs={autoCreateDefs} onChange={handleUpdate} />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '10px' }}>
          {userRole?.innovaAdmin && (
            <>
              {showImportAllHistoricalEDRData && (
                <Button variant='outlined' onClick={onImportHistorical} color='primary'>
                  Import All Historical EDR Data
                </Button>
              )}
              <Button variant='outlined' onClick={onSyncWellsAndReporting} color='primary'>
                Sync Wells and Reporting Data
              </Button>
            </>
          )}
          <Button variant='outlined' onClick={handleSave} disabled={!changesMade} color='primary'>
            Apply
          </Button>
          <Button onClick={onClose} variant='outlined' color='primary'>
            Close
          </Button>
        </DialogActions>
        <Backdrop
          open={isFetchingDefaults || isUpdatingDefaults || isLoadingErrorModelHeaders || isSyncingWellsAndReporting}
          sx={{
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.modal + 1,
            color: '#fff',
          }}>
          <CircularProgress color='inherit' />
        </Backdrop>
       <StatusSnackbar status={status} setStatus={setStatus} />
      </Dialog>
    </>
  )
}

export default DataConfigDefaultsModal
